import React, { useState, useEffect, useRef, type ReactNode } from "react";
import { useSelector } from "react-redux";
import { Rnd } from "react-rnd";
import styled from "styled-components";

import NavigateBefore from "./components/NavigateBefore";
import { useIsTwoColumn } from "../../hooks/useResponsiveLayout";

/**
 * 高さの設計:
 * - プロジェクト埋め込み（hasProjectBanner=true）: 親（ProjectWrapper の
 *   OutletContainer）がヘッダー / バナーを差し引いた高さを管理しているため、
 *   ここは 100% で親に追従する。100dvh からの直計算を自前で持つと、親より
 *   64px 高くなってドキュメント全体がはみ出し、グローバルヘッダーの出し入れの
 *   たびにウィンドウのスクロールバーが出たり消えたりして画面がズレる。
 * - スタンドアロン（/my-page 等）: 親（GlobalWrapper の ContentWrapper）が
 *   ヘッダー表示中に padding-top: 64px を足すため、その分を 100dvh から
 *   差し引く。transition は ContentWrapper の padding-top と同じ
 *   0.15s linear に揃え、アニメーション中も合計がちょうど 100dvh を保つ。
 */
const standaloneHeight = (scrollState: string) =>
  scrollState === "down"
    ? "100dvh"
    : "calc(100dvh - var(--global-header-height))";

const Container = styled.div<{
  $isSingleColumn: boolean;
  $hasProjectBanner: boolean;
  $scrollState: string;
}>`
  width: 100%;
  height: ${({ $hasProjectBanner, $scrollState }) =>
    $hasProjectBanner ? "100%" : standaloneHeight($scrollState)};
  ${({ $hasProjectBanner }) =>
    $hasProjectBanner ? "" : "transition: height 0.15s linear;"}
  background-color: var(--soremo-background);
  display: flex;
  flex-direction: ${({ $isSingleColumn }) =>
    $isSingleColumn ? "column" : "row"};
  justify-content: flex-start;
  position: relative;
  overflow: hidden;
`;

const LeftColumn = styled.div.attrs({
  // コンテンツ量の増減でスクロールバーが出入りしても幅が変わらないように
  className: "u-scrollbar-gutter-stable",
})<{
  $isSingleColumn: boolean;
}>`
  position: relative;
  /* 高さは Container（2 カラム時はさらに Rnd の height: 100%）に追従する */
  height: 100%;
  overflow-y: auto;
  width: ${({ $isSingleColumn }) => ($isSingleColumn ? "100%" : "auto")};
`;

const RightColumn = styled.div.attrs({
  className: "u-w100 u-relative u-scrollbar-gutter-stable",
})<{
  $isSingleColumn: boolean;
  $wide: boolean;
}>`
  /* 通常は読み幅 756px。表を持つページは wideRightColumn で全幅にする */
  max-width: ${({ $wide }) => ($wide ? "none" : "756px")};
  padding: ${({ $isSingleColumn }) =>
    $isSingleColumn ? "56px 16px 64px" : "24px 16px 64px"};
  margin-inline: auto;
  /* 高さは Container に追従する */
  height: 100%;
  overflow-y: auto;
`;

/**
 * 2 カラム時にレイアウト全体の右上へ重ねるスロット。左カラム内のヘッダー行に
 * 置くと幅が左カラム止まりになってしまう要素（プロジェクトメニュー等）を
 * ビューポート右端へ出すために使う。
 * top 10px はヘッダー行（48px）内でのアイコン縦位置 `(48 - 28) / 2`、
 * right 16px は本文の左右余白と揃えた値。
 */
const TopRightOverlay = styled.div`
  position: absolute;
  top: 10px;
  right: 16px;
  z-index: 10;
`;

interface TwoColumnLayoutProps {
  /** 左カラム（一覧側）のコンテンツ */
  leftColumnContent?: ReactNode;
  /** 右カラム（詳細側）のコンテンツ */
  rightColumnContent?: ReactNode;
  /**
   * 初期状態で右カラムを表示するかどうか。
   * 歴史的経緯で表示分岐には使われていない（2 カラム時は常に両方表示、
   * 1 カラム時は常に左カラムから開始）。呼び出し側との互換のために受け取る。
   */
  initialShowRightColumn?: boolean;
  /** 1 カラム時の「戻る」クリック時のコールバック */
  onBackClick?: () => void;
  /** 1 カラム時に左カラム上部へ表示するコンテンツ */
  mobileLeftHeader?: ReactNode;
  /** setShowRightColumn 関数を外部に公開するためのコールバック */
  getSetShowRightColumn?: ((setter: (show: boolean) => void) => void) | null;
  /** プロジェクトバナー（75px）の分だけ高さを詰めるかどうか */
  hasProjectBanner?: boolean;
  /**
   * 右カラムの読み幅制限（max-width: 756px）を外して全幅にするかどうか。
   * 表を含むページ（作品登録 / レビュー掲載 / 楽曲照合結果など）で使う。
   */
  wideRightColumn?: boolean;
  /**
   * 2 カラム時のみ、レイアウト右上（ビューポート右端）へ重ねる要素。
   * 1 カラム時は左カラムが画面幅いっぱいになるため描画しない
   * （呼び出し側が左カラムのヘッダー行へインラインで置く想定）。
   */
  topRightOverlay?: ReactNode;
}

/**
 * 2 カラムレイアウトコンポーネント。
 *
 * レイアウトの分岐は UA ではなくビューポート幅ベースの共通判定
 * `useIsTwoColumn`（幅 768px 以上かつスマホ UA でない）で行う:
 * - 2 カラム: 左（幅ドラッグ可変の Rnd）+ 右を並べ、左右それぞれが
 *   `overflow-y: auto` で独立してスクロールする
 * - 1 カラム: 左右を絶対配置 + translateX のスライドで切り替える
 *   （iPad などのタブレットは幅が足りていれば PC と同じ 2 カラムになる）
 */
const TwoColumnLayout: React.FC<TwoColumnLayoutProps> = ({
  leftColumnContent,
  rightColumnContent,
  onBackClick,
  mobileLeftHeader = null,
  getSetShowRightColumn = null,
  hasProjectBanner = false,
  wideRightColumn = false,
  topRightOverlay = null,
}) => {
  const isTwoColumn = useIsTwoColumn();
  const isSingleColumn = !isTwoColumn;

  // スタンドアロン時の高さ計算用（GlobalWrapper の padding-top と連動）。
  // 埋め込み（hasProjectBanner=true）では使わない。
  const scrollState = useSelector((state: { layout: string }) => state.layout);

  // 2 カラム時は左右とも常時表示のため、この state は 1 カラム時の
  // 左（一覧）⇄ 右（詳細）の切り替えにのみ効く。1 カラムは常に左から開始する。
  const [showRightColumn, setShowRightColumn] = useState(isTwoColumn);

  const [leftColumnWidth, setLeftColumnWidth] = useState<string | number>(
    "clamp(320px, 38.2vw, calc(1140px * 0.382))",
  );

  // setShowRightColumn関数を親コンポーネントに渡す
  useEffect(() => {
    if (getSetShowRightColumn) {
      getSetShowRightColumn(setShowRightColumn);
    }
  }, [getSetShowRightColumn]);

  // 1 カラム ⇄ 2 カラムの切り替わり（リサイズ / 回転）時に表示状態をリセット
  // （2 カラム化: 両方見えるので true / 1 カラム化: 一覧から開始）
  const previousIsTwoColumn = useRef(isTwoColumn);
  useEffect(() => {
    if (previousIsTwoColumn.current === isTwoColumn) return;
    previousIsTwoColumn.current = isTwoColumn;
    setShowRightColumn(isTwoColumn);
  }, [isTwoColumn]);

  // スクロール位置のリセット
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [showRightColumn]);

  const handleBackClick = () => {
    setShowRightColumn(false);
    if (onBackClick) {
      onBackClick();
    }
  };

  // 1 カラム表示時のスライド切り替え用スタイル。
  // 高さは Container（埋め込みは親追従 / スタンドアロンはヘッダー連動）に合わせる。
  const singleColumnLeftStyle: React.CSSProperties = {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    zIndex: showRightColumn ? 1 : 2,
    transform: showRightColumn ? "translateX(-100%)" : "translateX(0)",
    transition: "transform 0.3s ease-out",
  };

  const singleColumnRightStyle: React.CSSProperties = {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    zIndex: showRightColumn ? 2 : 1,
    transform: showRightColumn ? "translateX(0)" : "translateX(100%)",
    transition: "transform 0.3s ease-out",
  };

  return (
    <Container
      $isSingleColumn={isSingleColumn}
      $hasProjectBanner={hasProjectBanner}
      $scrollState={scrollState}
    >
      {isSingleColumn ? (
        // 1 カラム: positionとtransformを使用したスライド切り替え
        <>
          <div style={singleColumnLeftStyle}>
            <LeftColumn $isSingleColumn={true}>
              {mobileLeftHeader}
              {leftColumnContent}
            </LeftColumn>
          </div>
          <div style={singleColumnRightStyle}>
            <RightColumn $isSingleColumn={true} $wide={wideRightColumn}>
              <NavigateBefore onClick={handleBackClick} />
              {rightColumnContent}
            </RightColumn>
          </div>
        </>
      ) : (
        // 2 カラム: 左カラムは境界ドラッグで幅可変、左右は独立スクロール
        <>
          <Rnd
            // 幅は controlled（ドラッグで state 更新）、高さは常に Container に
            // 追従させる。size を渡さないと react-rnd がルートへ height: auto を
            // インラインで当て、% 高さの連鎖が切れて左カラムが伸びてしまう。
            size={{ width: leftColumnWidth, height: "100%" }}
            minWidth={320}
            maxWidth={640}
            enableResizing={{ right: true }}
            disableDragging={true}
            onResize={(_e, _direction, ref) => {
              setLeftColumnWidth(ref.offsetWidth);
            }}
            style={{ position: "relative" }}
          >
            <LeftColumn $isSingleColumn={false}>
              <div className="resize-handle"></div>
              {leftColumnContent}
            </LeftColumn>
          </Rnd>
          <RightColumn $isSingleColumn={false} $wide={wideRightColumn}>
            {rightColumnContent}
          </RightColumn>
          {topRightOverlay && (
            <TopRightOverlay>{topRightOverlay}</TopRightOverlay>
          )}
        </>
      )}
    </Container>
  );
};

export default TwoColumnLayout;
